import { useEffect } from "react";

import { flushResumeSave } from "@/client/services/resume/update";

/**
 * Make sure the builder's last edit reaches the server before the student leaves.
 *
 * WHY THIS EXISTS. Autosave is `debounce(updateResume, 500)` living inside the résumé store, and a
 * debounce is a timer — nothing about unmounting the builder or closing the tab makes it fire. Any
 * edit made in the final half-second is therefore dropped, silently.
 *
 * That window is small, but it is not randomly distributed: the moment someone is most likely to
 * leave is the moment right after their last action. In the AI optimization flow that action is
 * clicking Accept on a suggestion, so the suggestion that goes missing is preferentially the LAST
 * one they accepted — which reads as "the AI's changes don't save" rather than as a race.
 *
 * ⚠️ `pagehide`, NOT `beforeunload`. `beforeunload` is unreliable on mobile Safari and on tab
 * discards; `pagehide` fires for both, including a navigation into the back/forward cache. The
 * unmount cleanup covers the in-app case (client-side navigation back to the dashboard), which fires
 * no page event at all.
 *
 * Distinct from `useCoachUnloadGuard`, which PROMPTS the student not to leave during an LMS handoff.
 * This one asks nothing and applies to every résumé edit in the builder.
 */
export const useResumeSaveFlush = () => {
  useEffect(() => {
    const onPageHide = () => flushResumeSave();

    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      flushResumeSave();
    };
  }, []);
};
